import type { ChatHistory } from 'types/chatbot';

const MAX_HISTORY_STORAGE_SIZE = 500 * 1024;
const DEFAULT_SCOPE_ID = 'default';
const ASSISTANT_SCOPE_SEPARATOR = ':assistant:';

export type ChatbotContext = 'admin' | 'user';

export interface ChatbotIdentity {
  tenantId: string;
  userId: string;
}

export const getChatbotHistoryScope = (context: ChatbotContext, identity: ChatbotIdentity, moduleId?: string): string =>
  `${context}:tenant:${identity.tenantId}:user:${identity.userId}:module:${moduleId ?? DEFAULT_SCOPE_ID}`;

export const getAssistantHistoryScope = (historyScope: string, assistantId: string | null): string =>
  `${historyScope}${ASSISTANT_SCOPE_SEPARATOR}${assistantId ?? DEFAULT_SCOPE_ID}`;

const estimatePersistedStorageSize = (value: unknown): number => {
  try {
    const serialized = JSON.stringify(JSON.stringify(value));

    return serialized?.length ?? 0;
  } catch {
    return 0;
  }
};

export const trimChatHistoryToStorageLimit = (history: ChatHistory[], maxStorageSize = MAX_HISTORY_STORAGE_SIZE): ChatHistory[] => {
  if (estimatePersistedStorageSize(history) <= maxStorageSize) {
    return history;
  }

  let low = 0;
  let high = history.length;
  let bestFitLength = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidateHistory = history.slice(0, mid);

    if (estimatePersistedStorageSize(candidateHistory) <= maxStorageSize) {
      bestFitLength = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return history.slice(0, bestFitLength);
};

export const upsertChatHistory = (history: ChatHistory[], chat: ChatHistory, maxStorageSize = MAX_HISTORY_STORAGE_SIZE): ChatHistory[] => {
  const updatedHistory = [chat, ...history.filter((item) => item.id !== chat.id || item.scope !== chat.scope)];

  return trimChatHistoryToStorageLimit(updatedHistory, maxStorageSize);
};

export const filterChatHistoryByScope = (history: ChatHistory[], scope: string): ChatHistory[] => history.filter((chat) => chat.scope === scope);
